/** One Admin tab: a saved Admin, a pending invitation, or the unsent draft. `key` is a stable record id, never an array index. */
export interface AdminTabItem {
  key: string;
  label: string;
  kind: 'member' | 'pending' | 'draft';
}

/** How many options the "+N More" menu shows before it scrolls (Figma S03). */
export const MENU_VISIBLE_OPTIONS = 5;

/**
 * How many tabs fit in `available` pixels. Everything is shown when it all fits next to the Add action. Otherwise Add Admin moves INTO the
 * "+N More" menu, so only room for the "+N More" button itself is kept. At least one tab is always shown. Unknown geometry (a width of 0, such as before layout or in a test DOM)
 * shows every tab rather than hiding people.
 */
export function computeVisibleCount(widths: readonly number[], available: number, moreWidth: number, addWidth: number, gap: number): number {
  if (widths.length === 0 || available <= 0 || widths.every((w) => w <= 0)) return widths.length;
  const total = widths.reduce((sum, w) => sum + w + gap, 0);
  if (total + addWidth <= available) return widths.length;
  const room = available - moreWidth - gap;
  let used = 0;
  let count = 0;
  for (const w of widths) {
    if (used + w + gap > room) break;
    used += w + gap;
    count += 1;
  }
  return Math.max(1, count);
}

/** Indexes (into `items`) shown as tabs and shown in the menu. The active tab is always a visible tab: when it would be hidden it takes the
 * last visible slot and the tab it displaces moves into the menu, so the person you are editing never disappears behind "+N More". */
export function splitTabs(itemCount: number, visibleCount: number, activeIndex: number): { visible: number[]; hidden: number[] } {
  const all = Array.from({ length: itemCount }, (_, i) => i);
  if (visibleCount >= itemCount) return { visible: all, hidden: [] };
  let visible = all.slice(0, visibleCount);
  if (activeIndex >= visibleCount && activeIndex < itemCount) {
    visible = [...visible.slice(0, Math.max(0, visibleCount - 1)), activeIndex];
  }
  return { visible, hidden: all.filter((i) => !visible.includes(i)) };
}
